/**
 * NewVersionDialog — modal para abrir una versión draft v(N+1) sobre
 * un documento oficial vigente.
 *
 * Doc 07 §"Versionado de artefactos": el canon vigente (v(N)) NO se
 * afecta — sigue siendo el oficial visible hasta que la nueva versión
 * pase por flujo de aprobación. Si el user descarta el draft, el canon
 * sigue intacto.
 *
 * `change_reason` es obligatorio (mínimo 1 carácter, máximo 2000):
 * va a auditoría y es visible para los aprobadores en el flujo de
 * review. Cliente que audita ve por qué se reabrió el canon.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ArtifactSourceMode } from '../../../api/artifacts'
import { createNewVersion } from '../../../api/artifacts'
import { ApiError } from '../../../api/client'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { pushToast } from '../../../stores/toastStore'

interface NewVersionDialogProps {
  open: boolean
  onClose: () => void
  /** UUID del artifact con canon vigente. */
  artifactId: string
  /** UUID del proyecto (para invalidar queries al confirmar). */
  projectId: string
  /** Versión actual oficial — se muestra en el copy para que el user
   * sepa "vas a crear v(N+1) sobre v(N)". */
  currentVersionNumber: number
  /** OT-0007 — decide a dónde navega tras crear la v(N+1): un
   *  documento en modo carga no tiene `.../edit` (no hay secciones),
   *  se queda en el mismo visor que ya sabe operar sobre el draft
   *  activo (`ProjectOfficialArtifactView.tsx`). */
  sourceMode: ArtifactSourceMode
}

export function NewVersionDialog(props: NewVersionDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca limpia, sin
  // reiniciar el motivo ni el error con efectos.
  if (!props.open) return null
  return <NewVersionForm {...props} />
}

function NewVersionForm({
  open,
  onClose,
  artifactId,
  projectId,
  currentVersionNumber,
  sourceMode,
}: NewVersionDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [changeReason, setChangeReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return createNewVersion(accessToken, artifactId, {
        change_reason: changeReason.trim(),
      })
    },
    onSuccess: async (newVersion) => {
      // Invalidamos queries relevantes (artifact + lifecycle + canon).
      // En paralelo para no bloquear el navigate; los useQuery del
      // siguiente render del editView verán los datos frescos.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['artifacts', artifactId] }),
        queryClient.invalidateQueries({
          queryKey: ['projects', projectId, 'lifecycle'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['projects', projectId, 'canon'],
        }),
      ])
      // ORDEN CRÍTICO: navigate ANTES de onClose. Si onClose desmonta
      // el dialog y, por cadena, otros componentes ascendientes, el
      // `useNavigate` retornado puede quedar suspendido en un árbol
      // que se está desmontando — la navegación queda en limbo y el
      // user "se queda en el viewer". Empujar el route nuevo primero
      // garantiza que la transición ocurra antes del desmontaje.
      //
      // OT-0007 — modo carga no tiene `.../edit` (no hay secciones):
      // se queda en el visor, que ya muestra la v(N+1) recién creada
      // vía `active_draft_version_id`.
      if (sourceMode === 'upload') {
        await navigate({
          to: '/projects/$projectId/official/$artifactId',
          params: { projectId, artifactId },
        })
      } else {
        await navigate({
          to: '/projects/$projectId/official/$artifactId/edit',
          params: { projectId, artifactId },
          search: { v: newVersion.id },
        })
      }
      pushToast({
        kind: 'success',
        message: t('official_viewer.new_version.toast_success'),
      })
      onClose()
    },
    onError: (err: unknown) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback =
        err instanceof ApiError
          ? err.body?.message ?? err.message
          : err instanceof Error
            ? err.message
            : 'unknown'
      setError(
        code
          ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
          : fallback,
      )
    },
  })

  const trimmed = changeReason.trim()
  const canSubmit = trimmed.length > 0 && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? () => {} : onClose}
      title={t('official_viewer.new_version.dialog_title')}
    >
      <div className="space-y-4">
        <p
          className="font-body text-sm text-ink-muted"
          dangerouslySetInnerHTML={{
            __html: t('official_viewer.new_version.dialog_intro', {
              current: currentVersionNumber,
              next: currentVersionNumber + 1,
            }),
          }}
        />

        <div>
          <label
            htmlFor="new-version-reason"
            className="block text-xs font-medium uppercase tracking-wider text-ink-subtle"
          >
            {t('official_viewer.new_version.change_reason_label')}
          </label>
          <textarea
            id="new-version-reason"
            value={changeReason}
            onChange={(e) => {
              setChangeReason(e.target.value)
              if (error) setError(null)
            }}
            placeholder={t(
              'official_viewer.new_version.change_reason_placeholder',
            )}
            maxLength={2000}
            rows={4}
            disabled={mutation.isPending}
            className="mt-1 w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand disabled:opacity-60"
            autoFocus
          />
          <p className="mt-1 text-[11px] text-ink-subtle">
            {t('official_viewer.new_version.change_reason_help')}
          </p>
        </div>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-3 text-sm text-critical">
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('official_viewer.new_version.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="rounded-md bg-brand px-4 py-1.5 text-sm font-medium text-canvas hover:bg-brand-deep disabled:opacity-50"
          >
            {mutation.isPending
              ? t('official_viewer.new_version.creating')
              : t('official_viewer.new_version.confirm')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
